// PARTIDA AO VIVO.
//
// A tela agora CONDUZ a partida em vez de reproduzir um log pronto. A cada
// tique ela pede alguns segundos de jogo ao motor, e entre um tique e outro o
// tecnico pode pausar e trocar quem esta em quadra -- que e o motivo de o
// motor ter virado uma maquina de estado retomavel.
//
// O ritmo continua o do 38 Brasil: a partida inteira comprimida num tempo real
// escolhido, com a preferencia salva.
//
// Usa setInterval, NAO requestAnimationFrame: medido neste ambiente, o rAF
// entregou 1 quadro em 600 ms com a pagina visivel, e ele tambem congela em
// aba de fundo e em economia de bateria. Um relogio de partida que depende de
// quadros simplesmente para.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { stepMatch, courtState, substitute, matchResult, setTactics } from '../engine/game.js';
import { Intervalo, pararNosQuartos, setPararNosQuartos } from './intervalo.jsx';
import { formatMinutes } from '../engine/boxscore.js';
import { BoxScore } from './ui.jsx';
import { POSITION_LABEL } from '../engine/players.js';

const PACE_KEY = 'basquete82.ritmo';

export const PACE_OPTIONS = [
  { id: 'lenta', ms: 150000, label: 'Lenta', hint: '2min30' },
  { id: 'normal', ms: 90000, label: 'Normal', hint: '1min30' },
  { id: 'rapida', ms: 50000, label: 'Rapida', hint: '50s' },
  { id: 'turbo', ms: 25000, label: 'Turbo', hint: '25s' },
  { id: 'sonica', ms: 10000, label: 'Super sonica', hint: '10s' },
];

export function getPaceMs() {
  try {
    const raw = Number(localStorage.getItem(PACE_KEY));
    if (PACE_OPTIONS.some((o) => o.ms === raw)) return raw;
  } catch { /* armazenamento bloqueado */ }
  return 50000;
}
export function setPaceMs(ms) {
  try { localStorage.setItem(PACE_KEY, String(ms)); } catch { /* sem drama */ }
  return ms;
}

const TICK_MS = 100;

export function MatchLive({ live, config, userTeamId, onFinish }) {
  const { match } = live;
  const userIsHome = live.homeId === userTeamId;
  const which = userIsHome ? 'home' : 'away';

  const [paceMs, setPace] = useState(getPaceMs);
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(() => match.done);
  const [showBox, setShowBox] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  // O quarto que ACABOU, quando a partida está parada no intervalo.
  const [intervalo, setIntervalo] = useState(null);
  const [pararQuartos, setPararQuartos] = useState(pararNosQuartos);
  // A tática vale para a partida inteira, então vive aqui e não no laço.
  const [tactics, setTac] = useState(
    () => (userIsHome ? match.H : match.A).team.tactics
      || { pace: 'normal', offense: 'equilibrado', defense: 'equilibrada', focus: 'equilibrado' },
  );
  const [selOut, setSelOut] = useState(null);
  const [, force] = useState(0);
  const redesenha = () => force((n) => n + 1);
  const feedRef = useRef(null);

  const totalGameSeconds = useMemo(
    () => (config.rules?.quarterSeconds ?? 720) * 4,
    [config],
  );

  useEffect(() => {
    if (done || paused || subOpen || intervalo) return undefined;
    const porTique = (totalGameSeconds / paceMs) * TICK_MS;

    const id = setInterval(() => {
      /* O quarto é lido ANTES e DEPOIS do passo: um tique avança vários
         segundos de jogo e pode atravessar a virada do quarto inteira. Olhar
         só o valor depois deixaria a parada passar batido. */
      const antes = match.ctx.quarter;
      const r = stepMatch(match, { seconds: Math.max(1, porTique) });
      redesenha();
      if (r.done) { setDone(true); clearInterval(id); return; }
      if (pararQuartos && match.ctx.quarter > antes) {
        setIntervalo(antes);
        clearInterval(id);
      }
    }, TICK_MS);
    return () => clearInterval(id);
  }, [paused, done, paceMs, subOpen, match, totalGameSeconds, intervalo, pararQuartos]);

  useEffect(() => { if (feedRef.current) feedRef.current.scrollTop = 0; });

  const log = match.log || [];
  const hs = match.H.score;
  const as = match.A.score;
  const usScore = userIsHome ? hs : as;
  const themScore = userIsHome ? as : hs;
  const quarter = match.ctx.quarter;
  const clock = Math.max(0, match.ctx.clock);
  const court = courtState(match, which);
  const progresso = Math.min(100, (match.elapsed / totalGameSeconds) * 100);

  const pular = () => { stepMatch(match, { all: true }); setDone(true); redesenha(); };

  const trocar = (entraId) => {
    const r = substitute(match, which, selOut, entraId);
    if (r.ok) { setSelOut(null); setSubOpen(false); }
    redesenha();
    return r;
  };

  if (showBox) {
    return (
      <div className="wrap">
        <button className="primary" style={{ width: '100%', padding: 14, marginBottom: 12 }}
          onClick={() => onFinish(live)}>continuar</button>
        <div className="card" style={{ marginBottom: 30 }}><BoxScore box={matchResult(match)} /></div>
      </div>
    );
  }

  if (intervalo) {
    const side = userIsHome ? match.H : match.A;
    const outro = userIsHome ? match.A : match.H;
    const q = side.quarterScores;
    const qo = outro.quarterScores;
    return (
      <Intervalo
        quarto={intervalo}
        placar={{
          nos: usScore,
          eles: themScore,
          nomeNos: userIsHome ? match.homeTeam.name : match.awayTeam.name,
          nomeEles: userIsHome ? match.awayTeam.name : match.homeTeam.name,
        }}
        noQuarto={{ nos: q[q.length - 1] ?? 0, eles: qo[qo.length - 1] ?? 0 }}
        court={court.onCourt}
        banco={court.bench.filter((p) => !p.fouledOut)}
        tactics={tactics}
        onTactics={(nova) => { setTac(nova); setTactics(match, which, nova); redesenha(); }}
        onSubstituir={() => { setIntervalo(null); setSubOpen(true); setSelOut(null); }}
        onContinuar={() => setIntervalo(null)}
        onDesligar={() => { setPararQuartos(false); setPararNosQuartos(false); setIntervalo(null); }}
      />
    );
  }

  return (
    <div className="wrap">
      <div className="live">
        <div className="live-clock">
          <span className="q">{quarter <= 4 ? `${quarter}º quarto` : `Prorrogacao ${quarter - 4}`}</span>
          <span className="t tabular">{formatMinutes(clock)}</span>
        </div>
        <div className="live-score">
          <div className={`side ${usScore > themScore ? 'lead' : ''}`}>
            <div className="nm">{userIsHome ? match.homeTeam.name : match.awayTeam.name}</div>
            <div className="pt tabular">{usScore}</div>
          </div>
          <div className="x">×</div>
          <div className={`side ${themScore > usScore ? 'lead' : ''}`}>
            <div className="nm">{userIsHome ? match.awayTeam.name : match.homeTeam.name}</div>
            <div className="pt tabular">{themScore}</div>
          </div>
        </div>
        <div className="live-bar"><div style={{ width: `${progresso}%` }} /></div>
      </div>

      <div className="row wrap" style={{ gap: 6, margin: '12px 0' }}>
        {!done && (
          <button className={paused ? 'primary sm' : 'ghost sm'} onClick={() => setPaused((p) => !p)}>
            {paused ? 'continuar' : 'pausar'}
          </button>
        )}
        {!done && (
          <button className="primary sm" onClick={() => { setSubOpen(true); setSelOut(null); }}>
            substituir
          </button>
        )}
        {!done && <button className="ghost sm" onClick={pular}>pular para o fim</button>}
        {!done && (
          <button
            className={`sm ${pararQuartos ? 'primary' : 'ghost'}`}
            title={pararQuartos
              ? 'O jogo para no fim de cada quarto para você mexer.'
              : 'O jogo corre do início ao fim, sem parar nos quartos.'}
            onClick={() => { const novo = !pararQuartos; setPararQuartos(novo); setPararNosQuartos(novo); }}>
            intervalos: {pararQuartos ? 'sim' : 'não'}
          </button>
        )}
        {/* "continuar" e o que quase todo mundo quer depois do apito, entao e
            ele que leva o destaque. A sumula fica ao lado, para quem quer ver. */}
        {done && <button className="primary sm" onClick={() => onFinish(live)}>continuar</button>}
        {done && <button className="ghost sm" onClick={() => setShowBox(true)}>ver sumula</button>}
        <span className="spacer" />
        <select value={paceMs} style={{ width: 'auto' }}
          onChange={(e) => setPace(setPaceMs(Number(e.target.value)))}>
          {PACE_OPTIONS.map((o) => <option key={o.id} value={o.ms}>{o.label} ({o.hint})</option>)}
        </select>
      </div>

      {/* Quem esta em quadra agora -- com as faltas a vista, que e o que decide
          uma substituicao de verdade. */}
      <div className="card tight">
        <h3>Em quadra</h3>
        <div className="court">
          {court.onCourt.map((p) => (
            <div key={p.id} className={`court-p ${p.fouls >= 5 ? 'risco' : ''}`}>
              <span className="nm">{p.name}</span>
              <span className="ln">
                {p.pts} pts · {p.reb} reb · {p.ast} ast ·{' '}
                <b className={p.fouls >= 5 ? 'neg' : ''}>{p.fouls} faltas</b>
              </span>
            </div>
          ))}
        </div>
      </div>

      {subOpen && (
        <div className="card" style={{ borderColor: 'var(--accent)' }}>
          <div className="row spread" style={{ marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>{selOut ? 'Quem entra?' : 'Quem sai?'}</h3>
            <button className="ghost sm" onClick={() => { setSubOpen(false); setSelOut(null); }}>
              cancelar
            </button>
          </div>
          <div className="grid two">
            {(selOut ? court.bench : court.onCourt).map((p) => (
              <button
                key={p.id}
                className="club"
                disabled={p.fouledOut}
                onClick={() => (selOut ? trocar(p.id) : setSelOut(p.id))}
              >
                <div className="nm">{p.name}</div>
                <div className="small dim">{POSITION_LABEL[p.position]} · {p.overall} ovr</div>
                <div className="small dim">
                  {formatMinutes(p.seconds)} · {p.pts} pts ·{' '}
                  <b className={p.fouls >= 5 ? 'neg' : ''}>{p.fouls} faltas</b>
                  {p.fouledOut && <span className="neg"> · fora</span>}
                </div>
              </button>
            ))}
          </div>
          <div className="dim small" style={{ marginTop: 8 }}>
            O jogo fica pausado enquanto voce decide.
          </div>
        </div>
      )}

      <div className="feed" ref={feedRef}>
        {[...log].reverse().slice(0, 40).map((ev, i) => (
          <div key={log.length - i} className={`ev ${ev.type} ${i === 0 ? 'novo' : ''}`}>
            <span className="tm tabular">{ev.q}Q {formatMinutes(ev.clock)}</span>
            <span className="tx">{ev.text}</span>
            {ev.points ? <span className="pts">+{ev.points}</span> : null}
          </div>
        ))}
        {!log.length && <div className="muted small">Bola ao alto...</div>}
      </div>
    </div>
  );
}
